import React, { useMemo, useState, useEffect } from "react";
import { Outlet, Link as RouterLink, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import {
  Sidebar,
  SidebarSection,
  SidebarItem,
  IconButton,
  Breadcrumbs,
  TextInput,
} from "components";
import ManagerHeader from "../components/ManagerHeader";
import StatusMenu from "./StatusMenu";
import {
  selectGameId,
  selectGameObject,
} from "features/gamestate/gameStateSlice";
import {
  updateGameDetailsAction,
  selectSavingDetails,
} from "features/gameEditor/editorSlice";
import {
  getRoundKeys,
  getRoundCount,
  computeCompletion,
  touchedAtMs,
} from "../lifecycle";
import logging from "utils/logging";
import "./ManagedGameHome.css";

const log = logging.getLogger("ManagedGameHome");

const PencilGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const ManagedGameHome = () => {
  const gameObject = useSelector(selectGameObject);
  const gameId = useSelector(selectGameId);
  const saving = useSelector(selectSavingDetails);
  const dispatch = useDispatch();
  const location = useLocation();

  log.debug("ManagedGameHome gameObject:", gameObject);

  // Inline title editing in the sidebar header (mirrors the hero's name editor).
  const [editingTitle, setEditingTitle] = useState(false);
  const [nameDraft, setNameDraft] = useState(gameObject?.name || "");
  useEffect(() => {
    if (!editingTitle) setNameDraft(gameObject?.name || "");
  }, [gameObject?.name, editingTitle]);

  const isActive = (to) => location.pathname === to;

  const rounds = useMemo(() => getRoundKeys(gameObject || {}), [gameObject]);

  const homeTo = `/manage/game/${gameId}`;
  const isHome = location.pathname === homeTo || location.pathname === `${homeTo}/`;

  const saveTitle = async () => {
    const name = nameDraft.trim();
    if (name && name !== gameObject?.name) {
      await dispatch(updateGameDetailsAction(gameId, { name }));
    }
    setEditingTitle(false);
  };
  const cancelTitle = () => {
    setNameDraft(gameObject?.name || "");
    setEditingTitle(false);
  };

  const roundCount = getRoundCount(gameObject);
  const { total } = computeCompletion(gameObject);
  const touchedMs = touchedAtMs(gameObject);
  const titleMeta = [
    `${roundCount} ${roundCount === 1 ? "round" : "rounds"}`,
    `${total} clues`,
    touchedMs ? `Updated ${moment(touchedMs).fromNow()}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const lead = (
    <div className="gx-mgh-lead">
      <Breadcrumbs
        items={[
          { label: "My Games", to: "/manage" },
          { label: gameObject?.name || "Untitled Game", to: homeTo },
        ]}
      />
      <StatusMenu />
    </div>
  );

  return (
    <div className="gx-mgh surface-manager">
      <ManagerHeader breadcrumbs={lead} />

      <div className="gx-mgh-body">
        <Sidebar>
          <div className="gx-mgh-gametitle">
            {editingTitle ? (
              <form
                className="gx-mgh-gametitle-edit-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveTitle();
                }}
              >
                <TextInput
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={saveTitle}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") cancelTitle();
                  }}
                  disabled={saving}
                  autoFocus
                  aria-label="Game name"
                />
              </form>
            ) : (
              <div className="gx-mgh-gametitle-row">
                <RouterLink
                  to={homeTo}
                  className={`gx-mgh-gametitle-name ${isHome ? "is-active" : ""}`}
                >
                  {gameObject?.name || "Untitled Game"}
                </RouterLink>
                <IconButton
                  variant="ghost"
                  size="sm"
                  label="Edit game name"
                  className="gx-mgh-gametitle-edit"
                  onClick={() => setEditingTitle(true)}
                >
                  <PencilGlyph />
                </IconButton>
              </div>
            )}
            <div className="gx-mgh-gametitle-meta">{titleMeta}</div>
          </div>

          <SidebarSection>
            <SidebarItem
              as={RouterLink}
              to={homeTo}
              active={isHome}
              label="Game Home"
            />
          </SidebarSection>

          <SidebarSection label="Edit Game">
            {rounds.map((roundKey) => {
              const to = `/manage/game/${gameId}/editor/${roundKey}`;
              const cats = Array.isArray(gameObject?.categories?.[roundKey])
                ? gameObject.categories[roundKey]
                : [];
              let written = 0;
              let total = 0;
              for (const cat of cats) {
                for (let cell = 1; cell <= 5; cell += 1) {
                  total += 1;
                  if (cat?.[String(cell)]) written += 1;
                }
              }
              const num = roundKey.replace("round", "");
              const isComplete = total > 0 && written === total;
              const trailing =
                total > 0 ? (isComplete ? "✓" : `${written}/${total}`) : undefined;
              return (
                <SidebarItem
                  key={roundKey}
                  as={RouterLink}
                  to={to}
                  active={isActive(to)}
                  label={`Round ${num}`}
                  trailingLabel={trailing}
                  title={
                    total > 0 ? `${written} of ${total} clues written` : undefined
                  }
                />
              );
            })}
            <SidebarItem
              as={RouterLink}
              to={`/manage/game/${gameId}/editor/final`}
              active={isActive(`/manage/game/${gameId}/editor/final`)}
              label="Final Round"
              trailingLabel={
                gameObject?.categories?.final?.question ? "✓" : "0/1"
              }
              title={
                gameObject?.categories?.final?.question
                  ? "1 of 1 clues written"
                  : "0 of 1 clues written"
              }
            />
          </SidebarSection>

          <SidebarSection label="Configure">
            <SidebarItem
              as={RouterLink}
              to={`/manage/game/${gameId}/settings`}
              active={isActive(`/manage/game/${gameId}/settings`)}
              label="Settings"
            />
            <SidebarItem
              as={RouterLink}
              to={`/manage/game/${gameId}/control-room`}
              active={isActive(`/manage/game/${gameId}/control-room`)}
              label="Control Room"
            />
          </SidebarSection>
        </Sidebar>

        <main className="gx-mgh-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ManagedGameHome;
